/**
 * Utilidad para generar Request Nonce
 *
 * El Request Nonce es un valor único de un solo uso que previene ataques de replay.
 * Formato: {timestamp}-{random}
 *
 * Características:
 * - Único por petición
 * - Incluye timestamp para validación temporal
 * - Usa crypto API para aleatoriedad criptográfica
 * - El backend valida que no se reutilice (Redis TTL: 120 segundos)
 */

/**
 * Genera un string aleatorio criptográficamente seguro
 * @param {number} length - Longitud del string (en bytes, resultado será el doble en hex)
 * @returns {string} String hexadecimal aleatorio
 */
const generateRandomHex = (length = 16) => {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint8Array(length)
    crypto.getRandomValues(array)
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }

  // Fallback para entornos sin crypto API (no debería ocurrir en navegadores modernos)
  console.warn('[RequestNonce] crypto.getRandomValues not available, using Math.random (less secure)')
  let result = ''
  for (let i = 0; i < length * 2; i++) {
    result += Math.floor(Math.random() * 16).toString(16)
  }
  return result
}

/**
 * Genera un Request Nonce único
 *
 * Formato: {timestamp}-{random}
 * Ejemplo: 1714579200-a3f5c8d9e2b1f4a7c6d8e9f0a1b2c3d4
 *
 * @returns {string} Request Nonce único
 */
export const generateRequestNonce = () => {
  const timestamp = Math.floor(Date.now() / 1000) // Unix timestamp en segundos
  const random = generateRandomHex(16) // 32 caracteres hex

  return `${timestamp}-${random}`
}

/**
 * Valida el formato de un Request Nonce (solo validación de formato, no de uso)
 *
 * @param {string} nonce - Nonce a validar
 * @returns {boolean} true si el formato es válido
 */
export const isValidNonceFormat = (nonce) => {
  if (!nonce || typeof nonce !== 'string') {
    return false
  }

  // Formato: {timestamp}-{random}
  const parts = nonce.split('-')
  if (parts.length !== 2) {
    return false
  }

  const [timestamp, random] = parts

  // Validar timestamp (debe ser un número de 10 dígitos)
  if (!/^\d{10}$/.test(timestamp)) {
    return false
  }

  // Validar random (debe ser hexadecimal de 32 caracteres)
  if (!/^[a-f0-9]{32}$/i.test(random)) {
    return false
  }

  return true
}

/**
 * Extrae el timestamp de un Request Nonce
 *
 * @param {string} nonce - Nonce del que extraer el timestamp
 * @returns {number|null} Timestamp en segundos, o null si el formato es inválido
 */
export const extractNonceTimestamp = (nonce) => {
  if (!isValidNonceFormat(nonce)) {
    return null
  }

  const timestamp = parseInt(nonce.split('-')[0], 10)
  return timestamp
}
